import { Link, useLocation } from "react-router";
import { BadgeQuestionMark } from "lucide-react";
import Icon from "../Icon/Icon.jsx";
import { MODELS, useMultiModel } from "../../../api";
import styles from "./toolbar.css.ts";

/**
 * Toolbar component for displaying navigation buttons based on provided props.
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.smallTeam - Information about the small team.
 * @param {boolean} [props.showUploadButton=true] - Flag to show or hide the upload button.
 * @param {boolean} [props.showLibraryButton=true] - Flag to show or hide the library button.
 * @param {boolean} [props.showLernreiseAdminButtons=false] - Flag to show or hide the admin buttons in the Lernreise section.
 * @param {string} props.baseUrl - Which URL the Toolbar is displayed on
 * @param {string} [props.leaveTo=".."] - Which URL to navigate to on clicking leave
 * @param {boolean} [props.adminButtonsDisabled=false] - Whether to disable admin buttons (even if they are shown)
 * @param {boolean} [props.showGlossaryButton=false] - Flag to show or hide the glossary button.
 * @param {React.ReactNode} [props.classroomButton] - Slot rendered right of the glossary button in the admin section (adults only). Pass null to omit it.
 * @param {function} onDisabledButtonClick - What to do when a disabled button is clicked
 *
 * @returns {JSX.Element} Rendered Toolbar component.
 */
export const Toolbar = ({
  smallTeam,
  showUploadButton = true,
  showLibraryButton = true,
  showLernreiseAdminButtons = false,
  baseUrl,
  leaveTo = "..",
  adminButtonsDisabled = false,
  showGlossaryButton = false,
  classroomButton = null,
  onDisabledButtonClick,
}) => {
  // Get the base url for the toolbar links.
  const location = useLocation().pathname;

  const [showroomData] = useMultiModel([
    showUploadButton && smallTeam
      ? MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeam.id)
      : null,
  ]);

  const glossaryButton = showGlossaryButton ? (
    <Link
      id="glossary-button"
      className={styles.toolbarButton({
        active: location.startsWith(`${baseUrl}/glossary`),
      })}
      to={`${baseUrl}/glossary`}
    >
      <BadgeQuestionMark size={32} />
      Glossar
    </Link>
  ) : null;

  return (
    <nav aria-label="Toolbar" className={styles.toolbarBox} id="toolbar">
      <div className={styles.toolbarContainer}>
        <div id="basic-tools" className={styles.toolbarUsertour}>
          <Link
            id="basic-tools"
            className={styles.toolbarButton({
              active:
                location === baseUrl || location.startsWith(`${baseUrl}/step`),
              iconsLeftAndRight: true,
            })}
            to={baseUrl}
          >
            <Icon name="learningJourney" size="32px" />
            Lernreise
          </Link>
          {showLibraryButton && (
            <Link
              id="basic-tools"
              className={styles.toolbarButton({
                active: location.startsWith(`${baseUrl}/library`),
              })}
              to={`${baseUrl}/library`}
            >
              <Icon name="library" size="32px" />
              Bibliothek
            </Link>
          )}

          <>
            {!showLernreiseAdminButtons && (
              <Icon name="separator" size="32px" />
            )}

            <Link
              id="basic-tools"
              className={styles.toolbarButton({
                active: location.startsWith(`${baseUrl}/showroom`),
              })}
              to={`${baseUrl}/showroom`}
            >
              <Icon name="showroom" size="32px" />
              Showroom
            </Link>
          </>
        </div>

        {!showLernreiseAdminButtons && glossaryButton}

        {showLernreiseAdminButtons && (
          <>
            <Link
              id="handbook-button"
              className={styles.toolbarButton({
                active: location.startsWith(`${baseUrl}/handbook`),
              })}
              to="https://www.digital-sparks.org/handbuch"
              target="_blank"
            >
              <Icon name="handbook" size="32px" />
              Handbuch
            </Link>

            {glossaryButton}

            {classroomButton}

            <Icon name="separator" size="32px" />
            <Link
              id="group-button"
              className={styles.toolbarButton({
                active: location.startsWith(`${baseUrl}/invite`),
              })}
              to={adminButtonsDisabled ? null : `${baseUrl}/invite`}
              onClick={
                adminButtonsDisabled && onDisabledButtonClick
                  ? onDisabledButtonClick
                  : null
              }
            >
              <Icon name="teams" size="32px" />
              Einladen
            </Link>

            <Icon name="separator" size="32px" />

            <Link
              className={styles.toolbarButton({
                active: location.startsWith(`${baseUrl}/options`),
                iconsLeftAndRight: true,
              })}
              to={adminButtonsDisabled ? null : `${baseUrl}/options`}
              onClick={
                adminButtonsDisabled && onDisabledButtonClick
                  ? onDisabledButtonClick
                  : null
              }
              id="options-button"
            >
              <Icon name="options" size="32px" />
              Optionen
            </Link>
          </>
        )}

        {showUploadButton && smallTeam && (
          <Link
            className={styles.toolbarButton({
              active: location.startsWith(`${baseUrl}/team-diary`),
            })}
            to={`${baseUrl}/team-diary/${smallTeam.id}`}
          >
            <Icon
              name={
                showroomData && showroomData.entry ? "pencil" : "arrowupload"
              }
              size="32px"
            />
            {showroomData && showroomData.entry ? "Mein Beitrag" : "Hochladen"}
          </Link>
        )}

        <Link
          className={styles.toolbarButton({
            active: false,
            iconsLeftAndRight: true,
            grayBackground: true,
          })}
          to={leaveTo}
          id="leave-button"
        >
          <Icon name="vector" size="32px" />
          Verlassen
        </Link>
      </div>
    </nav>
  );
};
